import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SUBSCRIBABLE_EVENTS,
  type CreateWebhookData,
  type WebhookEndpoint,
} from '@services/webhookService';

interface WebhookFormDialogProps {
  open: boolean;
  /** Present when editing; absent when creating. */
  endpoint?: WebhookEndpoint | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (data: CreateWebhookData) => void;
  onClose: () => void;
}

/**
 * Create or edit a webhook endpoint (REV-442).
 *
 * The submit button is NEVER pre-disabled. That is the platform's form rule and
 * it is load-bearing here rather than ceremonial: the most likely reason this
 * form fails is that the URL resolves somewhere internal, which the client
 * cannot know — only the server resolves DNS. A button disabled on client-side
 * validity would still let that submission through, while blocking users whose
 * input is fine for reasons they cannot see. It disables only while the request
 * is in flight, and re-enables on failure.
 *
 * Errors are surfaced in a live region so a screen reader user learns the
 * submission failed without having to go hunting for the reason.
 */
export default function WebhookFormDialog({
  open,
  endpoint,
  submitting,
  error,
  onSubmit,
  onClose,
}: WebhookFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(endpoint);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setName(endpoint?.name ?? '');
    setUrl(endpoint?.url ?? '');
    setEventTypes(endpoint ? [...endpoint.eventTypes] : []);
    setClientErrors({});
  }, [open, endpoint]);

  const toggleEvent = (value: string) => {
    setEventTypes((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    );
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // Validated ON SUBMIT, never by disabling the button.
    const errors: Record<string, string> = {};
    if (!name.trim()) {
      errors.name = t('webhooks.form.nameRequired');
    }
    if (!url.trim()) {
      errors.url = t('webhooks.form.urlRequired');
    }
    setClientErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    onSubmit({ name: name.trim(), url: url.trim(), eventTypes });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="webhook-form-title"
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id="webhook-form-title">
          {isEdit ? t('webhooks.form.editTitle') : t('webhooks.form.createTitle')}
        </DialogTitle>
        <DialogContent>
          {/* MUI's Alert already carries role="alert", so it announces itself on
              mount. Wrapping it in a second role="alert" live region nests two
              of them and the message is announced twice. */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label={t('webhooks.form.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={Boolean(clientErrors.name)}
            helperText={clientErrors.name || t('webhooks.form.nameHelp')}
          />

          <TextField
            label={t('webhooks.form.url')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={Boolean(clientErrors.url)}
            helperText={clientErrors.url || t('webhooks.form.urlHelp')}
          />

          <FormControl component="fieldset" margin="normal" fullWidth>
            <FormLabel component="legend">{t('webhooks.form.events')}</FormLabel>
            <FormGroup>
              {SUBSCRIBABLE_EVENTS.map((event) => (
                <FormControlLabel
                  key={event.value}
                  control={
                    <Checkbox
                      checked={eventTypes.includes(event.value)}
                      onChange={() => toggleEvent(event.value)}
                      name={event.value}
                    />
                  }
                  label={t(event.labelKey)}
                />
              ))}
            </FormGroup>
            {/* Said plainly rather than enforced: an endpoint subscribed to
                nothing is a legitimate paused subscription, not an error. */}
            <FormHelperText>{t('webhooks.form.eventsHelp')}</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {/* outlined, not the MUI default text variant: a text button has
              no border or background and reads as prose (CSS-16). */}
          <Button variant="outlined" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          {/* Disabled ONLY while in flight — never on validity.
              While submitting its only content is a spinner, so it needs an
              explicit name: a button named solely by a progressbar descendant
              has no dependable accessible name. */}
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            aria-label={submitting ? t('webhooks.form.submitting') : undefined}
          >
            {submitting ? (
              <CircularProgress size={24} aria-label={t('webhooks.form.submitting')} />
            ) : (
              t(isEdit ? 'common.save' : 'webhooks.form.create')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
